import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useResource, useQueryParam } from 'react-resource-router';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { PAGE_QUERY_PARAM } from 'src/components/pipelines/constants';
import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { DeleteModal } from 'src/components/settings/modals';
import { useParams } from 'src/router/hooks';

import { useIsMergeQueueEmpty, useDeleteMergeQueue } from '../hooks';
import { messages } from '../merge-queues.i18n';
import { useMergeQueues } from '../provider';
import { mergeQueuesRouteResource } from '../resources';

export const MergeQueueDeleteModal = ({
  onClose,
  focusedMergeQueue,
}: {
  onClose: () => void;
  focusedMergeQueue: MergeQueue | undefined;
}) => {
  const { formatMessage } = useIntl();

  const [{ mergeQueues }] = useMergeQueues();

  const { repositoryOwner: owner, repositorySlug: slug } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
  }>();

  const [page, setPageParam] = useQueryParam(PAGE_QUERY_PARAM);

  const { refresh: refreshMergeQueues } = useResource(mergeQueuesRouteResource);

  const navigateToPreviousPage = useCallback(() => {
    if (!page) {
      refreshMergeQueues();
      return;
    }

    const currentPage = Number(page);
    if (currentPage > 1) {
      setPageParam(String(currentPage - 1));
    } else if (currentPage === 1) {
      setPageParam(undefined);
    }
  }, [page, refreshMergeQueues, setPageParam]);

  const { isMergeQueueEmpty, isMergeQueueEmptyLoading } = useIsMergeQueueEmpty({
    mergeQueueUuid: focusedMergeQueue?.uuid || '',
    owner,
    slug,
  });

  const { deleteMergeQueue, isDeletingMergeQueue } = useDeleteMergeQueue({
    mergeQueueUuid: focusedMergeQueue?.uuid || '',
    owner,
    slug,
    onClose,
    onSuccess:
      mergeQueues.length === 1 ? navigateToPreviousPage : refreshMergeQueues,
  });

  const shouldShowWarning = useMemo(
    () =>
      !isMergeQueueEmptyLoading &&
      (!isMergeQueueEmpty || focusedMergeQueue?.isActive),
    [isMergeQueueEmptyLoading, isMergeQueueEmpty, focusedMergeQueue?.isActive]
  );

  const modalBody = useMemo(
    () => (
      <Flex direction="column" gap="space.100">
        {formatMessage(messages.mergeQueueDeleteModalBody)}
        {shouldShowWarning && (
          <SectionMessage
            appearance="warning"
            title={formatMessage(messages.mergeQueueDeleteWarningTitle)}
          >
            <Box
              as="ul"
              xcss={xcss({
                paddingBlockStart: 'space.100',
                paddingInlineStart: 'space.200',
              })}
            >
              {!isMergeQueueEmpty && (
                <Box as="li">
                  {formatMessage(
                    messages.mergeQueueDeleteWarningForNonEmptyQueue
                  )}
                </Box>
              )}
              {focusedMergeQueue?.isActive && (
                <Box as="li">
                  {formatMessage(
                    messages.mergeQueueDeleteWarningForActiveQueue
                  )}
                </Box>
              )}
            </Box>
          </SectionMessage>
        )}
      </Flex>
    ),
    [
      isMergeQueueEmpty,
      focusedMergeQueue?.isActive,
      formatMessage,
      shouldShowWarning,
    ]
  );

  return (
    <DeleteModal
      header={formatMessage(messages.mergeQueueDeleteModalHeader)}
      body={modalBody}
      onConfirm={() =>
        focusedMergeQueue?.uuid && deleteMergeQueue(focusedMergeQueue.uuid)
      }
      onCancel={onClose}
      isDisabled={!focusedMergeQueue?.uuid || shouldShowWarning}
      isLoading={isMergeQueueEmptyLoading || isDeletingMergeQueue}
    />
  );
};
